<script>
import { mapGetters } from 'vuex';

import WorkflowNode from './Workflow.vue';
import ButtonNext from 'next/button/Button.vue';

export default {
  components: {
    WorkflowNode,
    ButtonNext,
  },
  props: {
    bot: {
      type: Object,
      required: true,
    },
  },
  emits: [
    'addWorkflow',
    'deleteStage',
    'editStage',
    'addStage',
    'deleteWorkflow',
    'editWorkflow',
  ],
  data() {
    return {
      selectedTabIndex: 0,
    };
  },
  computed: {
    ...mapGetters({
      workflows: 'wgptBotWorkflows/getWorkflows',
      uiFlags: 'wgptBotWorkflows/getUIFlags',
    }),
    // Only one workflow per bot is supported for now
    canAddWorkflow() {
      return !this.uiFlags.isFetching && this.workflows.length === 0;
    },
  },
  watch: {
    bot() {
      this.setDefaults();
    },
  },
  mounted() {
    this.setDefaults();
  },
  methods: {
    setDefaults() {
      this.$store.dispatch('wgptBotWorkflows/get', this.bot.id);
      this.$store.dispatch('wgptBotWorkflows/getLlmConfigs', this.bot.id);
    },
  },
};
</script>

<template>
  <div>
    <div class="workflow-nodes">
      <div
        class="workflow-node-header"
        :style="{ 'grid-column-end': workflows.length + 2 }"
      >
        <span
          class="inline-flex items-center px-3 py-1.5 mb-9 rounded-lg text-heading-3 text-n-blue-11 bg-n-blue-3 outline outline-1 -outline-offset-1 outline-n-blue-7"
        >
          {{ bot.name }}
        </span>
      </div>

      <WorkflowNode
        v-for="(workflow, index) in workflows"
        :key="workflow.id"
        :workflow="workflow"
        :class="{ 'right-node': index > workflows.length / 2 }"
        class="workflow-node"
        @edit="$emit('editWorkflow', workflow)"
        @delete="$emit('deleteWorkflow', workflow)"
        @add-stage="$emit('addStage', workflow)"
        @edit-stage="stage => $emit('editStage', stage, workflow)"
        @delete-stage="stage => $emit('deleteStage', stage, workflow)"
      />

      <div
        class="workflow-node workflow-node-button"
        :class="{ 'right-node': workflows.length }"
      >
        <ButtonNext
          class="macros__action-button mt-9 pointer-events-auto"
          color="teal"
          icon="i-lucide-circle-plus"
          :disabled="!canAddWorkflow"
          :label="
            $t('WGPT_BOTS.EDIT.TABS.WORKFLOWS.BUTTON_TEXT.CREATE_WORKFLOW')
          "
          @click="$emit('addWorkflow')"
        />
      </div>
    </div>
  </div>
</template>

<style scoped lang="scss">
.workflow-nodes {
  @apply grid grid-flow-col relative;
  grid-template-rows: auto minmax(0, 1fr);
  grid-auto-columns: minmax(375px, 1fr);
}

.workflow-node {
  @apply flex flex-col items-center row-start-2 relative pointer-events-none;
  min-width: 50%;

  &::before {
    @apply absolute h-8 border-t border-dashed border-n-slate-10;
    content: '';
    width: 50%;
  }
  &:not(.right-node)::before {
    @apply border-l border-dashed border-n-slate-10;
    @apply left-1/2;
  }
  &.right-node::before {
    @apply border-r border-dashed border-n-slate-10;
    @apply right-1/2;
  }

  &::after {
    @apply absolute h-8;
    @apply border-t border-dashed border-n-slate-10;
    content: '';
    width: 50%;
  }
  &:not(.right-node)::after {
    @apply left-0;
  }
  &.right-node::after {
    @apply right-0;
  }

  &:nth-child(2):after,
  &:last-child::after {
    display: none;
  }
}

.workflow-node-header {
  @apply flex justify-center row-start-1 col-start-1 relative;
  &::after {
    @apply absolute h-8 left-1/2 bottom-0;
    @apply border-l border-dashed border-n-slate-10;
    content: '';
    width: 0;
  }
}

.workflow-node-button {
  @apply flex flex-col items-center relative;
  &:not(.right-node)::before {
    width: 0;
    @apply left-1/2;
  }
}

[dir='rtl'] {
  .workflow-node {
    &:not(.right-node)::before {
      @apply right-1/2;
      @apply border-r border-dashed border-n-slate-10;
      @apply border-l-0;
    }
    &.right-node::before {
      @apply left-1/2 right-auto;
      @apply border-l border-dashed border-n-slate-10;
      @apply border-r-0;
    }

    &:not(.right-node)::after {
      @apply right-0;
    }
    &.right-node::after {
      @apply left-0 right-auto;
    }
  }
}
</style>
